import { z } from 'zod';
/**
 * Permission catalog entry. Spec §3.1, Appendix A. Permission names are the
 * scope-qualified verbs the API uses to gate handlers. The catalog is
 * seed-driven; runtime mutation is forbidden by the DB FK on
 * `role_permissions.permission_name`.
 */
export const permissionNameSchema = z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z][a-z0-9_]*:[a-z][a-z0-9_]*$/, 'permission name must be `<resource>:<verb>` in snake_case');
export const permissionSchema = z.object({
    name: permissionNameSchema,
    description: z.string().nullable(),
});
/**
 * Role row — a named bundle of permissions. Roles are seeded; the only
 * mutation surface is `PATCH /api/roles/:name` which updates description and
 * the permission set. Wildcards are NOT supported — the body schema strictly
 * whitelists names from the seeded catalog (matches the DB FK constraint).
 */
export const roleNameSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z][a-z0-9_]*$/, 'role name must be snake_case');
/**
 * Canonical role catalog. Mirrors `ROLES` in
 * `marketplace-api/src/db/seeds/roles-permissions.ts`. **Adding a role:**
 *   1. Append the literal here.
 *   2. Add a seed entry in `roles-permissions.ts:ROLES`.
 *   3. Update `ROLE_PERMISSION_MAP` to grant it permissions.
 *   4. Re-run `db:bootstrap`.
 */
export const ALL_ROLES = [
    'super_admin',
    'marketplace_admin',
    'team_admin',
    'developer',
    'viewer',
    'metrics_viewer',
];
/**
 * Zod enum mirroring `ALL_ROLES` — use this for input validation where the
 * role must be a known catalog entry (e.g. the `?roleName=` filter on
 * `GET /api/users`). The wider `roleNameSchema` regex still applies to
 * free-form text from the DB (the row read path, where the column carries
 * any snake_case string the schema accepts).
 */
export const roleNameEnumSchema = z.enum(ALL_ROLES);
export const roleSchema = z.object({
    name: roleNameSchema,
    description: z.string().nullable(),
    permissions: z.array(permissionNameSchema),
});
/**
 * Junction table row — `role_permissions`. Modelled for completeness; the
 * UI consumes the flattened `Role.permissions` projection above.
 */
export const rolePermissionSchema = z.object({
    roleName: roleNameSchema,
    permissionName: permissionNameSchema,
});
/**
 * `user_roles` row — the per-team grant. `teamId` is null for global grants
 * (e.g. super_admin). Used by `app.invalidateUserPermissions(userId)` cache
 * fan-out following a role mutation.
 */
export const userRoleSchema = z.object({
    userId: z.string().uuid(),
    roleName: roleNameSchema,
    teamId: z.string().uuid().nullable(),
});
//# sourceMappingURL=role.js.map